// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { useState } from "react";

const REPORTS = [
  { id:1, visitDate:"2026-02-06", client:"Canstorr Commodities", author:"Tyson Becker", status:"Submitted",
    objective:"Quarterly review of storage operations and Phase 2 expansion planning",
    attendees:["Tyson Becker","Lucas Mason","Canstorr Operations Manager"],
    discussion:["Reviewed current storage utilization at 85% capacity across all bins","Discussed Phase 2 expansion timeline targeting Q3 2026","CropPatrol sensor data accuracy validated at 99.2%","Transportation logistics for spring grain season"],
    outcomes:["Phase 2 budget approved internally by Canstorr","New sensor deployment scheduled for March","Transportation contract renewed for 12 months"],
    actionItems:[{text:"Send Phase 2 proposal document",assignee:"Lucas Mason",due:"2026-02-10"},{text:"Schedule sensor installation team",assignee:"Tyson Becker",due:"2026-02-20"}],
    nextSteps:"Follow up with Canstorr legal team on partnership agreement. Next quarterly visit scheduled for May 2026." },
  { id:2, visitDate:"2026-02-04", client:"Western Mining Corp", author:"Lee Mehling", status:"Reviewed",
    objective:"Product demonstration of OreSync safety and compliance tracking",
    attendees:["Lee Mehling","Western Mining Safety Director","Western Mining IT Manager"],
    discussion:["Demonstrated real-time safety monitoring dashboard","Reviewed compliance reporting automation features","Discussed integration with existing mine management systems","Pricing and licensing discussion for 3 mine sites"],
    outcomes:["Strong interest in safety monitoring module","IT team will evaluate API integration requirements","Budget approval expected by end of February"],
    actionItems:[{text:"Send technical documentation for API integration",assignee:"Jordan Lutz",due:"2026-02-08"},{text:"Prepare 3-site licensing proposal",assignee:"Lee Mehling",due:"2026-02-12"}],
    nextSteps:"Schedule follow-up demo with full IT team. Prepare customized pricing for multi-site deployment." },
  { id:3, visitDate:"2026-02-03", client:"Prairie Health Services", author:"Des Bachynski", status:"Submitted",
    objective:"Staff training on VitaSync platform and user onboarding",
    attendees:["Des Bachynski","Ryan Miskulin","PHS Training Coordinator","12 clinic staff"],
    discussion:["Conducted hands-on training session for 12 users","Covered patient tracking, appointment scheduling, and compliance modules","Addressed questions about data privacy and provincial health regulations","Set up admin accounts and permission levels"],
    outcomes:["12 users successfully onboarded","Training completion rate: 100%","3 minor UI issues identified and reported","Staff confidence rating: 4.2/5"],
    actionItems:[{text:"Fix 3 reported UI issues",assignee:"Jordan Lutz",due:"2026-02-10"},{text:"Send training follow-up materials",assignee:"Des Bachynski",due:"2026-02-07"}],
    nextSteps:"Schedule 2-week check-in call. Plan advanced features training for late February." },
  { id:4, visitDate:"2026-02-01", client:"AgriTech Farms", author:"Tyson Becker", status:"Reviewed",
    objective:"On-site CropPatrol sensor installation and calibration",
    attendees:["Tyson Becker","AgriTech Farm Manager"],
    discussion:["Installed 8 grain bin sensors across 4 storage facilities","Calibrated moisture and temperature readings","Configured alert thresholds for grain quality monitoring","Trained farm manager on mobile app usage"],
    outcomes:["All 8 sensors operational","Data accuracy confirmed within 0.5% tolerance","Farm manager comfortable with mobile monitoring","Estimated 15% reduction in grain spoilage losses"],
    actionItems:[{text:"Monitor sensor readings for 2 weeks",assignee:"Tyson Becker",due:"2026-02-15"},{text:"Schedule calibration follow-up",assignee:"Tyson Becker",due:"2026-03-01"}],
    nextSteps:"Two-week data review. If successful, expand to remaining 6 storage facilities." },
  { id:5, visitDate:"2026-01-30", client:"Metro Beauty Collective", author:"Lucas Mason", status:"Draft",
    objective:"Product demo of GlowDesk for multi-location salon chain",
    attendees:["Lucas Mason","Metro Beauty CEO","Metro Beauty Operations Manager"],
    discussion:["Presented GlowDesk booking and inventory management features","Demonstrated client history tracking and appointment scheduling","Discussed multi-location reporting and analytics","Pricing for 8-salon deployment reviewed"],
    outcomes:["Positive reception from leadership","Requested trial period for 2 locations","Need to add specific salon chair scheduling feature","Budget discussion deferred to next meeting"],
    actionItems:[{text:"Set up trial environment for 2 locations",assignee:"Jordan Lutz",due:"2026-02-10"},{text:"Prepare salon chair scheduling feature spec",assignee:"Lucas Mason",due:"2026-02-08"}],
    nextSteps:"Set up trial accounts. Follow up in 2 weeks for feedback. Prepare enterprise pricing proposal." },
  { id:6, visitDate:"2026-01-28", client:"Northern Fleet Co", author:"Lucas Mason", status:"Draft",
    objective:"Initial assessment of fleet management requirements",
    attendees:["Lucas Mason","Northern Fleet Operations Director"],
    discussion:["Assessed current fleet of 45 vehicles across 3 provinces","Reviewed existing GPS tracking and maintenance workflows","Identified pain points in fuel management and compliance reporting","Discussed Forge Fleet feature set and customization options"],
    outcomes:["Fleet of 45 vehicles confirmed as target deployment","Current system lacks real-time maintenance alerts","Compliance reporting is manual and time-consuming","Strong fit for Forge Fleet automated compliance module"],
    actionItems:[{text:"Prepare fleet management proposal",assignee:"Lucas Mason",due:"2026-02-05"},{text:"Research provincial compliance requirements",assignee:"Tyler Hodgson",due:"2026-02-07"}],
    nextSteps:"Send proposal. Schedule full product demo with operations team. Request fleet data export for migration planning." }
];

const STATUS_OPTIONS = ["All","Draft","Submitted","Reviewed"];

export default function VisitReportsPage() {
  const [reports] = useState(REPORTS);
  const [selectedReport, setSelectedReport] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [authorFilter, setAuthorFilter] = useState("All");

  const authors = ["All", ...new Set(reports.map(r => r.author))];
  const filtered = reports.filter(r => {
    if (statusFilter !== "All" && r.status !== statusFilter) return false;
    if (authorFilter !== "All" && r.author !== authorFilter) return false;
    return true;
  });

  const statusBadge = (status) => {
    const map = { Draft:"badge badge-yellow", Submitted:"badge badge-blue", Reviewed:"badge badge-green" };
    return map[status] || "badge";
  };

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Visit Reports</h1><p>Post-visit documentation and follow-ups</p></div>
        <button className="btn btn-primary">+ New Report</button>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Total Reports</div><div className="stat-value">{reports.length}</div></div>
        <div className="stat-card"><div className="stat-label">Drafts</div><div className="stat-value" style={{color:"#eab308"}}>{reports.filter(r=>r.status==="Draft").length}</div></div>
        <div className="stat-card"><div className="stat-label">Submitted</div><div className="stat-value" style={{color:"#3b82f6"}}>{reports.filter(r=>r.status==="Submitted").length}</div></div>
        <div className="stat-card"><div className="stat-label">Reviewed</div><div className="stat-value" style={{color:"#10b981"}}>{reports.filter(r=>r.status==="Reviewed").length}</div></div>
      </div>

      <div style={{display:"flex",gap:12,marginBottom:16}}>
        <select className="input" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{width:180}}>
          {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s === "All" ? "All Statuses" : s}</option>)}
        </select>
        <select className="input" value={authorFilter} onChange={e => setAuthorFilter(e.target.value)} style={{width:200}}>
          {authors.map(a => <option key={a} value={a}>{a === "All" ? "All Authors" : a}</option>)}
        </select>
      </div>

      <div className="grid-2">
        <div>
          {filtered.map(r => (
            <div key={r.id} className="card" style={{cursor:"pointer",border:selectedReport?.id===r.id?"1px solid #10b981":"1px solid transparent"}} onClick={() => setSelectedReport(r)}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                <div style={{fontSize:14,fontWeight:600,color:"#e2e8f0"}}>{r.client}</div>
                <span className={statusBadge(r.status)}>{r.status}</span>
              </div>
              <div style={{fontSize:12,color:"#64748b",display:"flex",gap:12}}>
                <span>Visit: {r.visitDate}</span>
                <span>By: {r.author}</span>
                <span>{r.actionItems.length} action items</span>
              </div>
            </div>
          ))}
        </div>

        <div>
          {selectedReport ? (
            <div className="card">
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:16}}>
                <div>
                  <h2 style={{fontSize:18,fontWeight:700,color:"#e2e8f0"}}>{selectedReport.client}</h2>
                  <div style={{fontSize:12,color:"#64748b",marginTop:2}}>Visit: {selectedReport.visitDate} | Author: {selectedReport.author}</div>
                </div>
                <span className={statusBadge(selectedReport.status)}>{selectedReport.status}</span>
              </div>

              <div style={{marginBottom:14}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Objective</h4>
                <p style={{fontSize:13,color:"#e2e8f0"}}>{selectedReport.objective}</p>
              </div>

              <div style={{marginBottom:14}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Attendees</h4>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>{selectedReport.attendees.map(a => <span key={a} className="badge badge-purple">{a}</span>)}</div>
              </div>

              <div style={{marginBottom:14}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Discussion Points</h4>
                {selectedReport.discussion.map((d, i) => (
                  <div key={i} style={{display:"flex",gap:8,padding:"4px 0"}}>
                    <span style={{color:"#10b981",fontSize:12}}>&#8226;</span>
                    <span style={{fontSize:13,color:"#e2e8f0"}}>{d}</span>
                  </div>
                ))}
              </div>

              <div style={{marginBottom:14}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Outcomes</h4>
                {selectedReport.outcomes.map((o, i) => (
                  <div key={i} style={{display:"flex",gap:8,padding:"4px 0"}}>
                    <span style={{color:"#3b82f6",fontSize:12}}>&#8226;</span>
                    <span style={{fontSize:13,color:"#e2e8f0"}}>{o}</span>
                  </div>
                ))}
              </div>

              <div style={{marginBottom:14}}>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:6}}>Action Items</h4>
                {selectedReport.actionItems.map((a, i) => (
                  <div key={i} style={{display:"flex",justifyContent:"space-between",padding:"6px 0",borderBottom:"1px solid #334155"}}>
                    <div><div style={{fontSize:13,color:"#e2e8f0"}}>{a.text}</div><div style={{fontSize:11,color:"#64748b"}}>{a.assignee}</div></div>
                    <span style={{fontSize:12,color:"#94a3b8"}}>Due: {a.due}</span>
                  </div>
                ))}
              </div>

              <div>
                <h4 style={{fontSize:13,fontWeight:700,color:"#94a3b8",marginBottom:4}}>Next Steps</h4>
                <p style={{fontSize:13,color:"#e2e8f0",lineHeight:1.6}}>{selectedReport.nextSteps}</p>
              </div>

              {selectedReport.status === "Draft" && (
                <div style={{marginTop:16,display:"flex",gap:8}}>
                  <button className="btn btn-primary">Submit for Review</button>
                  <button className="btn btn-outline">Edit Report</button>
                </div>
              )}
            </div>
          ) : (
            <div className="card" style={{textAlign:"center",padding:40,color:"#64748b"}}>
              <p>Select a report to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
